import { Box, TableCell, TableRow, Typography } from "@mui/material";
import React from "react";
import { CellType, GroupedReservations } from "../global/components/Tables";
import { useDateRange } from "../global/state";

const TIME_ZONE = "Europe/Belgrade";

const formatDateKey = (d: Date) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);

export class ColumnItem {
  constructor(public readonly id: number, public readonly cellType: CellType) {}
}

export interface TotalsRowProps {
  label?: string;
  groups: GroupedReservations[];
  menuContent: ColumnItem[];
  leftShadow?: boolean;
}

export const ReservationTotalRows: React.FC<TotalsRowProps> = ({
  groups,
  menuContent,
  label = "Ukupno",
  leftShadow,
}) => {
  const { dateRange } = useDateRange();

  const { totalsByDay, grandTotal } = React.useMemo(() => {
    const all = groups.flatMap((g) => g.reservations);
    const map = new Map<string, number>();
    for (const r of all) {
      const key = formatDateKey(new Date(r.bookingDate));
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    const totals = dateRange.workingDays.map(
      (d) => map.get(formatDateKey(d)) ?? 0
    );
    return { totalsByDay: totals, grandTotal: all.length };
  }, [groups, dateRange.workingDays]);

  return (
    <TableRow
      sx={{
        backgroundColor: "#F6F6F6",
        borderTop: "1px solid",
        borderColor: "shades.greyExtraLight",
      }}
    >
      <TableCell
        sx={{
          minHeight: "52px",
          padding: "0px 8px",
          position: "sticky",
          bottom: 0,
          left: 0,
          zIndex: 12,
          backgroundColor: "inherit",
          fontWeight: 700,
          ...(leftShadow
            ? {
                boxShadow: "3px 0px 3px rgba(20, 17, 16, 0.1)",
                borderRight: "1px solid",
                borderRightColor: "shades.greyExtraLight",
              }
            : {}),
        }}
      >
        {label}
      </TableCell>

      {menuContent.map((item) => (
        <TableCell
          key={item.id}
          sx={{
            padding: "10px 0px",
            minHeight: "52px",
            position: "sticky",
            bottom: 0,
            zIndex: 6,
            backgroundColor: "inherit",
          }}
        >
          {item.cellType === CellType.RESERVATIONS ? (
            <Box
              sx={{
                padding: "0px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: dateRange.dateCellWidth,
              }}
            >
              {totalsByDay.map((count, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "38px",
                  }}
                >
                  <Typography variant="overline" color="shades.dark">
                    {count}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </TableCell>
      ))}

      <TableCell
        sx={{
          position: "sticky",
          bottom: 0,
          right: 0,
          zIndex: 32,
          backgroundColor: "inherit",
          borderLeft: "1px solid",
          borderLeftColor: "shades.greyExtraLight",
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        {grandTotal}
      </TableCell>
    </TableRow>
  );
};
